// Argus — PowerShellWindow: terminal-like UI pentru remote PowerShell exec via WS direct.
// Bloc remote-powershell-shell 2026-05-08.
//
// Layout: <pre> output flex-grow + input row (TextField + Send + Cancel).
// Streaming chunks via CustomEvent argus:shell-chunk → append la output state.
// Final result: shell_exec_ws_result rezolvă promise → marker [exit code N · duration Ms].
// History ↑/↓ navighează commandHistory in-memory (NU localStorage — sensitive admin commands).

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { wsClient } from '../lib/wsClient';

interface OutputLine {
  kind: 'command' | 'stdout' | 'stderr' | 'exit' | 'error';
  text: string;
}

interface ShellChunkEvent {
  agentId: string;
  commandId: string;
  stream: 'stdout' | 'stderr';
  data: string;
}

function generateLocalCommandId(): string {
  // Same regex pattern ca wsClient (HEX 32 chars).
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined') crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

export function PowerShellWindow({ agentId, agentOnline }: { agentId: string; agentOnline: boolean }): JSX.Element {
  const [output, setOutput] = useState<OutputLine[]>([
    { kind: 'exit', text: '# PowerShell remote — Service SYSTEM context · max 30s default · max 1 MB output' },
  ]);
  const [input, setInput] = useState('');
  const [activeCommandId, setActiveCommandId] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [historyCursor, setHistoryCursor] = useState<number>(-1); // -1 = nu navigăm istoric
  const outputRef = useRef<HTMLPreElement | null>(null);
  const activeCommandIdRef = useRef<string | null>(null);

  // Sync ref pentru listener
  useEffect(() => { activeCommandIdRef.current = activeCommandId; }, [activeCommandId]);

  // Auto-scroll output la fund la fiecare update.
  useEffect(() => {
    const el = outputRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [output]);

  // Listener `argus:shell-chunk` → append la output dacă match agentId + commandId.
  useEffect(() => {
    function onChunk(ev: Event): void {
      const detail = (ev as CustomEvent<ShellChunkEvent>).detail;
      if (!detail) return;
      if (detail.agentId.toUpperCase() !== agentId.toUpperCase()) return;
      if (detail.commandId !== activeCommandIdRef.current) return;
      setOutput((prev) => [...prev, { kind: detail.stream, text: detail.data }]);
    }
    window.addEventListener('argus:shell-chunk', onChunk);
    return () => window.removeEventListener('argus:shell-chunk', onChunk);
  }, [agentId]);

  const handleSend = async (): Promise<void> => {
    const cmd = input.trim();
    if (!cmd || !agentOnline || activeCommandId !== null) return;

    const cmdId = generateLocalCommandId();
    setActiveCommandId(cmdId);
    setOutput((prev) => [...prev, { kind: 'command', text: `PS> ${cmd}` }]);
    setHistory((h) => [cmd, ...h.filter((c) => c !== cmd).slice(0, 99)]);
    setHistoryCursor(-1);
    setInput('');

    try {
      const result = await wsClient.requestShellExec(agentId, cmdId, cmd);
      const sec = (result.durationMs / 1000).toFixed(2);
      const note = result.truncated ? ' · output truncated at 1 MB' : '';
      setOutput((prev) => [...prev, {
        kind: result.exitCode === 0 ? 'exit' : 'error',
        text: `[exit ${result.exitCode} · ${sec}s${note}]`,
      }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setOutput((prev) => [...prev, { kind: 'error', text: `[error: ${msg}]` }]);
    } finally {
      setActiveCommandId(null);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!activeCommandId) return;
    try {
      await wsClient.requestShellCancel(agentId, activeCommandId);
      setOutput((prev) => [...prev, { kind: 'error', text: '[cancel sent]' }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setOutput((prev) => [...prev, { kind: 'error', text: `[cancel failed: ${msg}]` }]);
    }
  };

  const handleClear = (): void => {
    setOutput([{ kind: 'exit', text: '# Cleared — type a new PowerShell command' }]);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
      return;
    }
    if (e.key === 'ArrowUp' && !e.shiftKey) {
      if (history.length === 0) return;
      e.preventDefault();
      const next = Math.min(historyCursor + 1, history.length - 1);
      setHistoryCursor(next);
      setInput(history[next] ?? '');
      return;
    }
    if (e.key === 'ArrowDown' && !e.shiftKey) {
      if (historyCursor <= 0) {
        setHistoryCursor(-1);
        setInput('');
        return;
      }
      e.preventDefault();
      const next = historyCursor - 1;
      setHistoryCursor(next);
      setInput(history[next] ?? '');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!agentOnline && <Alert severity="warning" sx={{ borderRadius: 0 }}>Agent offline.</Alert>}
      <Box
        component="pre"
        ref={outputRef}
        data-allow-select="true"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'auto',
          margin: 0,
          padding: 1.5,
          backgroundColor: '#0F172A',
          color: '#E2E8F0',
          fontFamily: 'Consolas, "Cascadia Code", "Fira Code", monospace',
          fontSize: 13,
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {output.map((line, idx) => (
          <span
            key={idx}
            style={{
              color: line.kind === 'command' ? '#60A5FA'
                : line.kind === 'stderr' ? '#F87171'
                : line.kind === 'exit' ? '#A3E635'
                : line.kind === 'error' ? '#FBBF24'
                : '#E2E8F0',
              display: 'block',
            }}
          >
            {line.text}
          </span>
        ))}
        {activeCommandId !== null && (
          <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>… running</span>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: 1, borderColor: 'divider', alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={agentOnline ? 'Enter PowerShell command... (↑/↓ history, Enter send)' : 'Agent offline'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!agentOnline || activeCommandId !== null}
          inputRef={(el) => { if (el && !activeCommandId) (el as HTMLInputElement).focus({ preventScroll: true }); }}
          slotProps={{
            input: { sx: { fontFamily: 'Consolas, "Cascadia Code", monospace', fontSize: 13 } },
          }}
        />
        {activeCommandId !== null ? (
          <Tooltip title="Cancel running command">
            <IconButton color="error" onClick={() => void handleCancel()}>
              <StopCircleIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton color="primary" onClick={() => void handleSend()} disabled={!agentOnline || !input.trim()}>
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Clear output">
          <IconButton size="small" onClick={handleClear} disabled={activeCommandId !== null}>
            <ClearAllIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {activeCommandId !== null && <CircularProgress size={20} />}
      </Box>
      <Box sx={{ px: 1.5, py: 0.5, borderTop: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">
          Service SYSTEM context · max 30s timeout · max 1 MB output · history {history.length}/100
        </Typography>
      </Box>
    </Box>
  );
}
